import { Breadcrumbs } from '@mantine/core';
import { IconPlus, IconRefresh } from '@tabler/icons-react';
import type { BlockVolume } from '@xver/apis/v1/block-storage/model';
import { AccessBadge, Button } from '@xver/components/Atoms';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { useActiveProject, useProductAccess } from '@xver/hooks/useActiveProject';
import useBreadcrumbItems from '@xver/hooks/useBreadcrumbItems';
import usePageHeading from '@xver/hooks/usePageHeading';
import { usePricingQuery } from '@xver/hooks/usePricingQuery';
import { createBlockedReason, resolveQuota } from '@xver/utils/blockStorage';
import { focusAfterRender } from '@xver/utils/projects';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatBaht } from '../ContainerEngineInstancePage/utils/volumeCost';
import AttachmentSummary from './components/AttachmentSummary';
import AttachVolumeModal from './components/AttachVolumeModal';
import CreateVolumeModal from './components/CreateVolumeModal';
import DeleteVolumeModal from './components/DeleteVolumeModal';
import DetachVolumeModal from './components/DetachVolumeModal';
import ResizeVolumeModal from './components/ResizeVolumeModal';
import VolumeRowActions, { type VolumeAction } from './components/VolumeRowActions';
import VolumeStatusBadge from './components/VolumeStatusBadge';
import VolumesLoadError from './components/VolumesLoadError';
import { useBlockStorageRate } from './hooks/useBlockStorageRate';
import { useBlockVolumesQuery } from './hooks/useBlockVolumesQuery';
import { useVolumeStatusAnnouncement } from './hooks/useVolumeStatusAnnouncement';

const ROW_ACTIONS: readonly VolumeAction[] = ['attach', 'resize', 'delete'];

export default function BlockStoragePage() {
    const { t } = useTranslation('blockStorage');
    const title = t('page.title');
    const headingRef = usePageHeading(title);
    const breadcrumbItems = useBreadcrumbItems([
        { title: t('page.breadcrumbProducts'), href: '#' },
        { title, href: '/block-storage' },
    ]);
    const access = useProductAccess('block_storage');
    const { project } = useActiveProject();
    const rate = useBlockStorageRate();
    const canCreate = access === 'manage';
    const hasAccess = access !== undefined && access !== 'none';

    const { data, isPending, isError, refetch } = useBlockVolumesQuery({
        enabled: hasAccess,
    });
    const volumes = data?.data ?? [];
    const announcement = useVolumeStatusAnnouncement(data?.data);

    const { data: pricing } = usePricingQuery();
    const reportedQuota = data?.quota;
    const quota = resolveQuota(reportedQuota, pricing?.products.block_storage);
    const createBlocked = canCreate && reportedQuota ? createBlockedReason(reportedQuota) : null;
    const createBlockedId = useId();

    const retryButtonId = useId();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const refresh = async () => {
        setIsRefreshing(true);
        try {
            await refetch();
        } finally {
            setIsRefreshing(false);
        }
    };

    const [createOpened, setCreateOpened] = useState(false);
    const [modal, setModal] = useState<{ action: VolumeAction; volume: BlockVolume } | null>(null);
    const target = (action: VolumeAction) => (modal?.action === action ? modal.volume : null);
    const closeModal = () => setModal(null);

    const columns: DataTableColumn<BlockVolume>[] = [
        {
            key: 'name',
            label: t('table.name'),
            render: (volume) => (
                <span className="font-medium text-fg text-sm break-all">{volume.name}</span>
            ),
        },
        {
            key: 'size',
            label: t('table.size'),
            render: (volume) => (
                <span className="font-mono text-sm text-nowrap">
                    {t('sizeValue', { size: volume.sizeGiB })}
                </span>
            ),
        },
        {
            key: 'status',
            label: t('table.status'),
            render: (volume) => <VolumeStatusBadge volume={volume} />,
        },
        {
            key: 'attachment',
            label: t('table.attachedTo'),
            render: (volume) => <AttachmentSummary attachment={volume.attachment} />,
        },
        {
            key: 'cost',
            label: t('table.monthlyCost'),
            align: 'right',
            render: (volume) => (
                <span className="font-mono text-sm text-nowrap">
                    {t('perMonth', { amount: formatBaht(volume.monthlyPrice) })}
                </span>
            ),
        },
    ];

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin={'xs'} className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <div className="w-full flex flex-col lg:flex-row justify-between gap-2 lg:gap-0">
                    <div className="flex flex-col justify-start items-start gap-1">
                        <div className="flex flex-row flex-wrap items-center gap-2">
                            <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                                {title}
                            </h1>
                            {access && access !== 'manage' && <AccessBadge level={access} />}
                        </div>
                        <p className="w-auto max-w-135 text-base text-fg/68">
                            {t('page.description')}
                        </p>
                        {reportedQuota && (
                            <p className="w-auto max-w-135 text-sm text-fg/78">
                                {t('page.usage', {
                                    used: reportedQuota.usedGiB,
                                    limit: reportedQuota.limitGiB,
                                    volumes: reportedQuota.volumes,
                                    maxVolumes: reportedQuota.maxVolumes,
                                })}
                            </p>
                        )}
                        {createBlocked && (
                            <p id={createBlockedId} className="w-auto max-w-135 text-sm text-fg/78">
                                {t(`blocked.${createBlocked}`, {
                                    volumes: quota.volumes,
                                    maxVolumes: quota.maxVolumes,
                                    limit: quota.limitGiB,
                                })}
                            </p>
                        )}
                        <p className="w-auto max-w-135 text-sm text-fg/68">
                            {t('pricingNote', { rate: formatBaht(rate) })}
                        </p>
                    </div>
                    {hasAccess && (
                        <div className="flex flex-row flex-wrap justify-start lg:justify-end items-end gap-2">
                            <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<IconRefresh stroke={1.5} aria-hidden="true" />}
                                onClick={() => void refresh()}
                                isLoading={isRefreshing}
                            >
                                {t('page.refresh')}
                            </Button>
                            {canCreate && (
                                <Button
                                    size="sm"
                                    leftIcon={<IconPlus stroke={1.5} aria-hidden="true" />}
                                    aria-disabled={createBlocked ? true : undefined}
                                    aria-describedby={createBlocked ? createBlockedId : undefined}
                                    style={
                                        createBlocked
                                            ? { opacity: 0.5, cursor: 'not-allowed' }
                                            : undefined
                                    }
                                    onClick={
                                        createBlocked ? undefined : () => setCreateOpened(true)
                                    }
                                >
                                    {t('page.create')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="w-full">
                <p role="status" className="sr-only">
                    {announcement}
                </p>
                {access === 'none' ? (
                    <p className="max-w-[80ch] text-base text-fg/78">
                        {project
                            ? t('page.noAccess', { project: project.name })
                            : t('page.noAccessNoProject')}
                    </p>
                ) : isError && !data ? (
                    <VolumesLoadError
                        retryButtonId={retryButtonId}
                        onRetry={async () => {
                            const result = await refetch();
                            focusAfterRender(() =>
                                result.isError
                                    ? document.getElementById(retryButtonId)
                                    : headingRef.current
                            );
                        }}
                    />
                ) : (
                    <div className="w-full rounded-md bg-surface-1 border-[0.5px] border-line overflow-hidden">
                        <DataTable
                            caption={t('table.caption')}
                            columns={columns}
                            rows={volumes}
                            isLoading={isPending}
                            rowKey={(volume) => volume.id}
                            emptyMessage={t(canCreate ? 'table.empty' : 'table.emptyView')}
                            actionsLabel={t('table.actions')}
                            actions={
                                volumes.some((volume) => volume.access === 'manage')
                                    ? (volume) =>
                                          volume.access === 'manage' ? (
                                              <VolumeRowActions
                                                  volume={volume}
                                                  actions={ROW_ACTIONS}
                                                  quota={quota}
                                                  onAction={(action) =>
                                                      setModal({ action, volume })
                                                  }
                                              />
                                          ) : null
                                    : undefined
                            }
                        />
                    </div>
                )}
            </div>

            <CreateVolumeModal
                opened={createOpened}
                quota={quota}
                onClose={() => setCreateOpened(false)}
            />
            <AttachVolumeModal
                opened={!!target('attach')}
                volume={target('attach')}
                onClose={closeModal}
            />
            <DetachVolumeModal volume={target('detach')} onClose={closeModal} />
            <ResizeVolumeModal volume={target('resize')} quota={quota} onClose={closeModal} />
            <DeleteVolumeModal
                volume={target('delete')}
                onClose={closeModal}
                onDeleted={() => {
                    closeModal();
                    focusAfterRender(() => document.getElementById('main-content'));
                }}
            />
        </div>
    );
}
